import { useState, useEffect } from 'react';

const navItems = [
  { label: 'Sobre', href: '#sobre' },
  { label: 'Tratamentos', href: '#tratamentos' },
  { label: 'Raio-X Metabólico', href: '#raio-x' },
  { label: 'Depoimentos', href: '#depoimentos' },
  { label: 'Contato', href: '#contato' },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Colors: light text over dark hero background, then dark background with light text
  const textColor = scrolled ? 'rgba(248, 247, 245, 0.50)' : 'rgba(248, 247, 245, 0.55)';
  const textHoverColor = '#C8A96A';
  const logoColor = scrolled ? '#F8F7F5' : '#F8F7F5';

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 transition-all duration-500"
        style={{
          backgroundColor: scrolled ? 'rgba(14, 20, 18, 0.82)' : 'transparent',
          borderBottom: scrolled ? '1px solid rgba(200, 169, 106, 0.08)' : '1px solid transparent',
          backdropFilter: scrolled ? 'blur(20px) saturate(1.1)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(20px) saturate(1.1)' : 'none',
        }}
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-[72px] flex items-center justify-between">
          {/* Logo */}
          <a
            href="#"
            className="text-lg tracking-[0.15em] whitespace-nowrap transition-colors duration-500"
            style={{
              fontFamily: 'var(--font-heading)',
              color: logoColor,
              fontWeight: 500,
              opacity: 0.9,
            }}
          >
            Dra. Melissa Ciríaco
          </a>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              <a
                key={item.label}
                href={item.href}
                className="text-sm tracking-[0.05em] whitespace-nowrap transition-all duration-300 cursor-pointer"
                style={{
                  fontFamily: 'var(--font-body)',
                  color: textColor,
                  fontWeight: 400,
                }}
                onMouseEnter={(e) => {
                  (e.target as HTMLElement).style.color = textHoverColor;
                }}
                onMouseLeave={(e) => {
                  (e.target as HTMLElement).style.color = textColor;
                }}
              >
                {item.label}
              </a>
            ))}
          </div>

          {/* CTA Button */}
          <a
            href="https://wa.me/5585999372109?utm_source=site&utm_medium=navbar&utm_content=agendar_consulta"
            target="_blank"
            rel="noopener noreferrer"
            className="hidden md:inline-flex items-center px-6 py-2.5 rounded-full text-sm font-medium tracking-[0.04em] whitespace-nowrap transition-all duration-300 cursor-pointer"
            style={{
              fontFamily: 'var(--font-body)',
              backgroundColor: scrolled ? 'rgba(200, 169, 106, 0.12)' : 'rgba(200, 169, 106, 0.08)',
              color: '#C8A96A',
              border: scrolled ? '1px solid rgba(200, 169, 106, 0.2)' : '1px solid rgba(200, 169, 106, 0.15)',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget;
              el.style.backgroundColor = 'rgba(200, 169, 106, 0.18)';
              el.style.borderColor = 'rgba(200, 169, 106, 0.35)';
            }}
            onMouseLeave={(e) => {
              const el = e.currentTarget;
              if (scrolled) {
                el.style.backgroundColor = 'rgba(200, 169, 106, 0.12)';
                el.style.borderColor = 'rgba(200, 169, 106, 0.2)';
              } else {
                el.style.backgroundColor = 'rgba(200, 169, 106, 0.08)';
                el.style.borderColor = 'rgba(200, 169, 106, 0.15)';
              }
            }}
          >
            Agendar Consulta
          </a>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden w-10 h-10 flex items-center justify-center cursor-pointer"
            aria-label="Menu"
          >
            <div className="flex flex-col gap-[5px]">
              <span
                className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  mobileOpen ? 'rotate-45 translate-y-[3.25px]' : ''
                }`}
                style={{ backgroundColor: '#F8F7F5' }}
              />
              <span
                className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  mobileOpen ? 'opacity-0' : ''
                }`}
                style={{ backgroundColor: '#F8F7F5' }}
              />
              <span
                className={`block w-5 h-[1.5px] transition-all duration-300 ${
                  mobileOpen ? '-rotate-45 -translate-y-[3.25px]' : ''
                }`}
                style={{ backgroundColor: '#F8F7F5' }}
              />
            </div>
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`fixed inset-0 z-40 transition-all duration-500 md:hidden ${
          mobileOpen
            ? 'opacity-100 pointer-events-auto'
            : 'opacity-0 pointer-events-none'
        }`}
        style={{
          backgroundColor: '#0E1412',
        }}
      >
        <div className="flex flex-col items-center justify-center h-full gap-8">
          {navItems.map((item, i) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => setMobileOpen(false)}
              className="text-xl tracking-[0.08em] transition-all duration-500"
              style={{
                fontFamily: 'var(--font-heading)',
                color: '#F8F7F5',
                fontWeight: 400,
                transitionDelay: `${i * 80}ms`,
                opacity: mobileOpen ? 1 : 0,
                transform: mobileOpen ? 'translateY(0)' : 'translateY(10px)',
              }}
            >
              {item.label}
            </a>
          ))}
          <a
            href="https://wa.me/5585999372109?utm_source=site&utm_medium=navbar_mobile&utm_content=agendar_consulta"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setMobileOpen(false)}
            className="mt-4 px-8 py-3 rounded-full text-sm font-medium tracking-[0.03em] whitespace-nowrap transition-all duration-300"
            style={{
              backgroundColor: 'rgba(200, 169, 106, 0.15)',
              color: '#C8A96A',
              fontFamily: 'var(--font-body)',
              border: '1px solid rgba(200, 169, 106, 0.25)',
            }}
          >
            Agendar Consulta
          </a>
        </div>
      </div>
    </>
  );
}